import React, { useMemo } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { unified } from "unified";
import rehypeParse from "rehype-parse";
import rehypeSanitize from "rehype-sanitize";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";
import { type Root } from "hast";
import { type Components } from "react-markdown";
import { QCFinding } from "../../types/qcTypes";
import { rehypeQcFindings } from "./qcRehypePlugin";
import { qcSanitizeSchema } from "./qcRedlineShared";

// HTML content renderer for QC findings. Integrated/quick_action sections may be
// raw HTML (generated-document content). We parse the HTML *with source
// positions* (rehype-parse tags text nodes with the same offsets the backend
// measured findings against), sanitize, then rehypeQcFindings overlays the
// redline spans by offset -- identical machinery to the markdown branch, only
// the front parser differs. The resulting hast is rendered to React via
// hast-util-to-jsx-runtime (the same renderer react-markdown uses), so the
// shared QcSpan components and inline action icons work here too.

interface HtmlRedlineContentProps {
  findings: QCFinding[];
  sectionContent: string;
  components: Components;
  className?: string;
}

const HtmlRedlineContent: React.FC<HtmlRedlineContentProps> = ({
  findings,
  sectionContent,
  components,
  className,
}) => {
  const rendered = useMemo(() => {
    try {
      const processor = unified()
        .use(rehypeParse, { fragment: true })
        .use(rehypeSanitize, qcSanitizeSchema)
        .use(rehypeQcFindings, findings);
      const tree = processor.runSync(
        processor.parse(sectionContent) as Root,
      );
      return toJsxRuntime(tree, {
        Fragment,
        jsx,
        jsxs,
        components: components as never,
      });
    } catch (err) {
      // Defensive: never blank the panel if parsing/rendering fails -- fall
      // back to plain text so the finding remains reviewable.
      console.error("QC HTML render failed; falling back to text:", err);
      return <>{sectionContent}</>;
    }
  }, [findings, sectionContent, components]);

  return <div className={className}>{rendered}</div>;
};

export default HtmlRedlineContent;
